import { gql } from '@apollo/client';
import { NextPage } from 'next';
import Link from 'next/link';
import Router from 'next/router';
import styled from 'styled-components';
import { FOOTER_QUERY } from '../components/footer';
import { Footer } from '../components/footer';
import { Header, HEADER_QUERY } from '../components/header';
import { Col, ResponsiveContainer } from '../components/responsive';
import { Button } from '../components/ui/button';
import { Icon } from '../components/ui/icon';
import { ContentStackClient } from '../lib/contentstack';
import {
  PricingFeatures,
  PricingFeaturesFeaturesList,
} from '../types/contentstack';
import { IPageProps } from '../types/page';
import { classnames } from '../utils/classnames';
import { mediaScreen } from '../utils/mediaScreen';
import { Plan } from './pricing';

export const Page: NextPage<IPricingFeatures> = ({ header, page, footer }) => {
  const groupedFeatures = getFeaturesGroupedByCategory();

  function getFeaturesGroupedByCategory() {
    const groupedFeatures: { [key: string]: PricingFeaturesFeaturesList[] } =
      {};

    page?.features?.list?.forEach((feature) => {
      if (!groupedFeatures[feature?.category!]) {
        groupedFeatures[feature?.category!] = [feature!];
      } else {
        groupedFeatures[feature?.category!].push(feature!);
      }
    });

    return groupedFeatures;
  }

  function renderFeatureAvailability(x?: string | null) {
    if (!x || x?.toLowerCase() === 'no') {
      return '-';
    }

    if (x?.toLowerCase() === 'yes') {
      return <Icon name="checkmark" size={22} color="#51c0ac" />;
    }

    return x;
  }

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <Hero>
        <WidthDelimiter>
          <ResponsiveContainer>
            <Col xs={12}>
              <div className="button">
                <Link href={page?.hero?.button?.href!}>
                  <Icon name="arrow-left" size={20} />{' '}
                  {page?.hero?.button?.title}
                </Link>
              </div>

              <HeroTitle
                dangerouslySetInnerHTML={{ __html: page?.hero?.title ?? '' }}
              />

              <HeroText>{page?.hero?.text}</HeroText>
            </Col>
          </ResponsiveContainer>
        </WidthDelimiter>
      </Hero>

      <WidthDelimiter>
        <ResponsiveContainer>
          <FeaturesTable>
            <tbody>
              <tr className="plans">
                <td className="heading">
                  <h3>{page?.features?.title}</h3>
                  <p>{page?.features?.text}</p>
                </td>
                <td>
                  <Plan title="Starter" />
                </td>
                <td>
                  <Plan title="Pro" />
                </td>
                <td>
                  <Plan title="Enterprise" />
                </td>
              </tr>

              {Object.keys(groupedFeatures).map((category) => {
                const features = groupedFeatures[category];

                return (
                  <>
                    <tr className="category" key={category}>
                      <td colSpan={4}>{category}</td>
                    </tr>

                    {features.map((feature, idx) => (
                      <tr
                        key={feature.title}
                        className={classnames({
                          feature: true,
                          even: (idx + 1) % 2 === 0,
                        })}
                      >
                        <td>{feature.title}</td>
                        <td>{renderFeatureAvailability(feature.starter)}</td>
                        <td>{renderFeatureAvailability(feature.pro)}</td>
                        <td>{renderFeatureAvailability(feature.enterprise)}</td>
                      </tr>
                    ))}

                    <tr className="spacer">
                      <td colSpan={4} />
                    </tr>
                  </>
                );
              })}
            </tbody>
          </FeaturesTable>
        </ResponsiveContainer>
      </WidthDelimiter>

      <CTAFooter>
        <h2>{page?.footer?.title}</h2>
        <p>{page?.footer?.text}</p>

        <Button
          mType="secondary"
          onClick={() => Router.push(page?.footer?.button?.href!)}
        >
          {page?.footer?.button?.title}
        </Button>
      </CTAFooter>

      <Footer sections={footer?.sections} />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const { data: pageData } =
      await ContentStackClient().query<IPricingFeatures>({
        query: PAGE_QUERY,
      });

    return {
      props: {
        ...pageData,
      },
    };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query pricingQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: pricing_features(uid: "bltcc33a97e615e60f1") {
      title
      description
      hero {
        title
        text
        button {
          title
          href
        }
      }
      features {
        title
        text
        list {
          category
          title
          starter
          pro
          enterprise
        }
      }
      footer {
        title
        text
        button {
          title
          href
        }
      }
    }
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
`;

const Hero = styled.div`
  width: 100%;
  padding-top: 160px;
  padding-bottom: 50px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  > div {
    width: 100%;
  }

  .button {
    width: 100%;
    display: flex;
    margin-bottom: 50px;

    > a {
      display: flex;
      color: ${(props) => props.theme.colors.teal};
      cursor: pointer;
      align-items: center;
      font-size: 17px;

      .icon {
        margin-right: 5px;
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 120px;
  }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 67px;
  font-weight: bold;
  line-height: 1.19;
  margin-top: 0;
  text-align: center;

  strong {
    color: ${(props) => props.theme.colors.teal};
  }

  ${mediaScreen('xs', 'sm')} {
    font-size: 35px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 50px;
  }
`;

const HeroText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 1.5;
  margin-top: 50px;
  font-weight: 400;
  margin-bottom: 44px;
  text-align: center;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    max-width: 100%;
    margin-top: 24px;
    margin-bottom: 32px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    margin-top: 24px;
    margin-bottom: 32px;
  }
`;

const FeaturesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  z-index: 10;
  position: relative;

  .plans {
    > td {
      width: 25%;
      padding-left: 25px;
      vertical-align: bottom;
      padding-bottom: 35px;
    }

    .heading {
      h3 {
        color: ${(props) => props.theme.colors.teal};
        margin: 0 0 12px 0;
      }

      p {
        margin: 0;
      }
    }

    .plan-card {
      width: 100%;
      padding: 15px;
      height: 100%;
      justify-content: space-between;

      .title {
        font-size: 28px;
        margin-bottom: 0;
      }

      div {
        display: none !important;
      }
    }
  }

  .category {
    background: ${(props) => props.theme.colors.slate2};
    font-weight: bold;
    text-transform: uppercase;
    font-size: 14px;
    height: 42px;

    td {
      vertical-align: middle;
      padding-left: 12px;
    }
  }

  .spacer td {
    height: 50px;
  }

  .feature {
    &.even {
      background: ${(props) => props.theme.colors.slate2};
    }

    td {
      height: 42px;
      font-size: 14px;
      padding-left: 25px;
      text-align: center;
      vertical-align: middle;

      &:first-of-type {
        text-align: left;
        position: relative;

        &::after {
          content: '';
          border-right: 2px solid ${(props) => props.theme.colors.slate3};
          position: absolute;
          right: 0;
          top: 18%;
          bottom: 18%;
        }
      }

      > div {
        margin: 0 auto;
      }
    }
  }

  ${mediaScreen('xs', 'md')} {
    margin-left: -20px;
    width: calc(100% + 40px);

    .plans {
      td {
        height: 140px;
        padding: 0;
        padding-bottom: 20px;
      }

      .heading * {
        display: none;
      }

      .plan-card {
        background: none;
        margin-bottom: 0 !important;
        padding: 0;
        padding-left: 15px;

        .title {
          font-size: 16px;
          margin-bottom: 10px;
        }

        .price {
          font-size: 16px;

          &.words {
            font-size: 12px;
            margin-top: 6px;
          }
        }

        .term-length {
          font-size: 10px;
        }

        .button {
          margin-top: 15px;
          font-size: 10px;
          height: 22px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .text {
          display: none;
        }
      }
    }

    .category {
      font-size: 12px;
      font-weight: normal;
      letter-spacing: 1px;

      td {
        padding: 10px;
      }
    }

    .feature {
      td {
        font-size: 12px;
        padding: 10px;
        letter-spacing: 1px;
      }
    }
  }
`;

const CTAFooter = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 100px;
  z-index: 10;
  position: relative;

  h2 {
    font-size: 38px;
    margin-bottom: 20px;
  }

  p {
    font-size: 22px;
    margin-bottom: 30px;
  }
`;

interface IPricingFeatures extends IPageProps<PricingFeatures> {}

export default Page;
